import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type CustomRole,
  useCustomRoles,
  useRemoveMember,
  useTeams,
  useUpdateMember,
  useWorkspaceMembers,
  WorkspaceRoleSelect,
} from '@/entities/collab';
import { useViewer } from '@/entities/viewer';
import type { Workspace } from '@/entities/workspace';
import { FormError } from '@/shared/forms';
import { can, PERMISSIONS } from '@/shared/lib/permissions';
import { Avatar, Button, ClippedText, ErrorState, LoadingState } from '@/shared/ui';

import styles from './Admin.module.css';
import { assignableStandardRoles, isGrantableRole } from './grantable-roles';

/**
 * Members with their ONE workspace role (a standard role or a custom one, chosen in a single
 * searchable control), teams and the actions the viewer may take.
 */
export function MembersTab({ workspace }: { workspace: Workspace }) {
  const { t } = useTranslation();
  const viewer = useViewer();
  const viewerId = viewer.status === 'authenticated' ? viewer.user.id : null;
  const members = useWorkspaceMembers(workspace.id);
  const roles = useCustomRoles(workspace.id);
  const teams = useTeams(workspace.id);
  const update = useUpdateMember(workspace.id);
  const remove = useRemoveMember(workspace.id);
  const [search, setSearch] = useState('');
  const canAssign = can(workspace.permissions, PERMISSIONS.workspaceAssignRoles);
  const canRemove = can(workspace.permissions, PERMISSIONS.workspaceManageMembers);
  const standardAllowed = assignableStandardRoles(workspace);
  const customRoles = useMemo(() => roles.data ?? [], [roles.data]);
  const roleNames = useMemo(() => new Map(customRoles.map((r) => [r.id, r.name])), [customRoles]);

  const grantable = (role: CustomRole): boolean => isGrantableRole(workspace, role);

  const visible = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();
    return (members.data ?? []).filter(
      (m) =>
        needle === '' ||
        m.display_name.toLocaleLowerCase().includes(needle) ||
        m.login.toLocaleLowerCase().includes(needle),
    );
  }, [members.data, search]);

  if (members.isPending) return <LoadingState />;
  if (members.isError) return <ErrorState error={members.error} />;
  return (
    <div className={styles.panel}>
      <FormError error={update.error ?? remove.error} />
      {members.data.length > 6 && (
        <input
          type="search"
          className={styles.field}
          value={search}
          placeholder={t('workspaceAdmin.searchMembers')}
          aria-label={t('workspaceAdmin.searchMembers')}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />
      )}
      <ul className={styles.list}>
        {visible.map((member) => {
          const isSelf = member.user_id === viewerId;
          const isOwner = member.role === 'owner';
          const memberTeams = (teams.data ?? []).filter((team) =>
            member.team_ids.includes(team.id),
          );
          const roleName =
            member.role !== null
              ? t(`roles.${member.role}`)
              : (roleNames.get(member.custom_role_id ?? '') ?? t('roles.custom'));
          return (
            <li key={member.user_id} className={styles.memberRow} data-testid="member-row">
              <Avatar id={member.user_id} name={member.display_name} />
              <span className={styles.grow}>
                <span>
                  {member.display_name}
                  {isSelf && ` (${t('access.you')})`}
                </span>
                <span className={styles.meta}>@{member.login}</span>
                {memberTeams.length > 0 && (
                  <span className={styles.chips}>
                    {memberTeams.map((team) => (
                      <span key={team.id} className={styles.chip}>
                        <ClippedText>
                          {team.leader_user_id === member.user_id
                            ? `${team.name} · ${t('workspaceAdmin.leaderBadge')}`
                            : team.name}
                        </ClippedText>
                      </span>
                    ))}
                  </span>
                )}
              </span>
              <span className={styles.memberRole}>
                {canAssign && !isOwner && !isSelf ? (
                  <WorkspaceRoleSelect
                    label={t('access.roleOf', { name: member.display_name })}
                    value={{ role: member.role, customRoleId: member.custom_role_id }}
                    customRoles={customRoles}
                    standardRoles={standardAllowed}
                    isGrantable={grantable}
                    disabled={update.isPending}
                    onChange={(choice) => {
                      if (choice.customRoleId !== null) {
                        update.mutate({
                          userId: member.user_id,
                          custom_role_id: choice.customRoleId,
                        });
                      } else if (choice.role !== null) {
                        update.mutate({ userId: member.user_id, role: choice.role });
                      }
                    }}
                  />
                ) : (
                  <span className={styles.roleText}>
                    {roleName}
                    <span className={styles.chip}>
                      {isOwner
                        ? t('workspaceAdmin.roleProtected')
                        : member.role !== null
                          ? t('workspaceAdmin.roleStandard')
                          : t('workspaceAdmin.roleCustom')}
                    </span>
                  </span>
                )}
              </span>
              <span className={styles.memberActions}>
                {!isOwner && (isSelf || canRemove) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={remove.isPending}
                    onClick={() => {
                      remove.mutate(member.user_id);
                    }}
                  >
                    {isSelf ? t('workspaceAdmin.leave') : t('workspaceAdmin.removeMember')}
                  </Button>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
